import * as React from 'react'
import classNames from 'classnames'
import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import { Spinner } from '../ui/Spinner'
import { SVG_Magnifier2 } from '../svg/SVG_Magnifier2'
import { SVG_Plus } from '../svg/SVG_Plus'
import { SVG_TodoList } from '../svg/SVG_TodoList'
import { SVG_Warning } from '../svg/SVG_Warning'
import { SVG_LightningHollow } from '../svg/SVG_LightningHollow'

interface Props {
    deps: CommonDeps
    active: BottomBarButton
    routeToSearchTopPage: () => void
    routeToSearchSubpage: () => void
    routeToTodoTopPage: () => void
    routeToTodoSubpage: () => void
    routeToNotificationsPage: () => void
    routeToActionsPage: () => void
    startWorkflow: () => Promise<void>
    notificationsCount: number
}

interface State {
    startWorkflowStatus: 'ready' | 'loading' | 'failed'
}

export type BottomBarButton = 'todo' | 'search' | 'notifications' | 'actions'

export class BottomBar extends React.Component<Props, State> {
    public render = () => renderCatch(this.props.deps, this.tryRender)

    public state: State = {
        startWorkflowStatus: 'ready',
    }

    private onStartWorkflow = async () => {
        this.setState({ startWorkflowStatus: 'loading' })

        try {
            await this.props.startWorkflow()
            this.setState({ startWorkflowStatus: 'ready' })
        } catch (err) {
            this.setState({ startWorkflowStatus: 'failed' })
            this.props.deps.reportError(err, {})
        }
    }

    private renderTodoButton = () => (
        <div
            className={classNames('AppCore__page__BottomBar__button', 'todo', {
                active: this.props.active === 'todo',
            })}
            onClick={this.props.active === 'todo' ? this.props.routeToTodoTopPage : this.props.routeToTodoSubpage}
        >
            <SVG_TodoList />
        </div>
    )

    private renderSearchButton = () => (
        <div
            className={classNames('AppCore__page__BottomBar__button', 'search', {
                active: this.props.active === 'search',
            })}
            onClick={this.props.active === 'search' ? this.props.routeToSearchTopPage : this.props.routeToSearchSubpage}
        >
            <SVG_Magnifier2 />
        </div>
    )

    private renderStartWorkflowButton = () => (
        <div
            data-testid={'bottom-bar-start-workflow-button'}
            className={classNames('AppCore__page__BottomBar__start-workflow', {
                failed: this.state.startWorkflowStatus === 'failed',
            })}
            onClick={this.onStartWorkflow}
        >
            {this.state.startWorkflowStatus === 'ready' && (
                <div className="AppCore__page__BottomBar__plus">{<SVG_Plus />}</div>
            )}
            {this.state.startWorkflowStatus === 'loading' && (
                <div className="AppCore__page__BottomBar__spinner">
                    <Spinner size={28} thickness={2} color={'#95a5b4'} />
                </div>
            )}
            {this.state.startWorkflowStatus === 'failed' && (
                <div className="AppCore__page__BottomBar__error">
                    <SVG_Warning />
                </div>
            )}
        </div>
    )
    private renderActionsButton = () => (
        <div
            className={classNames('AppCore__page__BottomBar__button', 'actions', {
                active: this.props.active === 'actions',
            })}
            onClick={this.props.routeToActionsPage}
            data-testid={'bottom-bar-action-button'}
        >
            <SVG_LightningHollow />
        </div>
    )

    private renderNotificationsButton = () => (
        <div
            className={classNames('AppCore__page__BottomBar__button', 'notifications', {
                active: this.props.active === 'notifications',
            })}
            onClick={this.props.routeToNotificationsPage}
        >
            <zaptic-notification-bell
                active={this.props.active === 'notifications'}
                count={this.props.notificationsCount}
            ></zaptic-notification-bell>
        </div>
    )

    public tryRender = () => {
        return (
            <div className="AppCore__page__BottomBar">
                {this.renderTodoButton()}
                {this.renderSearchButton()}
                {this.renderStartWorkflowButton()}
                {this.renderActionsButton()}
                {this.renderNotificationsButton()}
            </div>
        )
    }
}
